import React, { useEffect, useRef, useState, useCallback } from 'react';
import DrawCanvas from './DrawCanvas';

/**
 * DiagramModal - author diagrams for notes/questions, two ways:
 *   ✏️ Draw    - point-and-click canvas (circles, lines, arrows, labels…)
 *   TikZ Code  - LaTeX/TikZ compiled in-browser via TikZJax (power users)
 *
 * Either way the result exports as a PNG that flows through the existing
 * image upload pipeline — mobile renders it with zero backend/app changes.
 *
 * @param {boolean} isOpen   - Whether the modal is open
 * @param {function} onClose - Called when modal is closed
 * @param {function} onInsert - Called with { dataUrl, name, source }
 */

const TIKZJAX_BASE = 'https://tikzjax.com/v1';
const RENDER_TIMEOUT_MS = 30000;
const EXPORT_SCALE = 3; // rasterize at 3x for crisp mobile rendering

// CSEC-geometry starter templates
const TEMPLATES = [
  {
    name: 'Circle',
    code: `\\begin{tikzpicture}[scale=1.2]
  \\draw[thick] (0,0) circle (2cm);
  \\fill (0,0) circle (2pt) node[below left] {$O$};
  \\draw[thick] (0,0) -- node[above] {$r$} (2,0);
\\end{tikzpicture}`,
  },
  {
    name: 'Chord & tangent',
    code: `\\begin{tikzpicture}[scale=1.2]
  \\draw[thick] (0,0) circle (2cm);
  \\fill (0,0) circle (2pt) node[below] {$O$};
  % chord AB
  \\draw[thick] (140:2) node[above left] {$A$} -- (40:2) node[above right] {$B$};
  % tangent at T
  \\coordinate (T) at (-90:2);
  \\fill (T) circle (2pt) node[below right] {$T$};
  \\draw[thick] ($(T)+(-2.4,0)$) -- ($(T)+(2.4,0)$);
  \\draw[dashed] (0,0) -- (T);
\\end{tikzpicture}`,
  },
  {
    name: 'Triangle',
    code: `\\begin{tikzpicture}[scale=1.2]
  \\coordinate (A) at (0,0);
  \\coordinate (B) at (4,0);
  \\coordinate (C) at (1.2,2.6);
  \\draw[thick] (A) node[below left] {$A$}
    -- (B) node[below right] {$B$}
    -- (C) node[above] {$C$} -- cycle;
  % angle marks
  \\draw (0.5,0) arc (0:65:0.5);
  \\node at (0.85,0.32) {$\\alpha$};
\\end{tikzpicture}`,
  },
  {
    name: 'Right triangle',
    code: `\\begin{tikzpicture}[scale=1.2]
  \\coordinate (A) at (0,0);
  \\coordinate (B) at (4,0);
  \\coordinate (C) at (0,3);
  \\draw[thick] (A) -- node[below] {$a$} (B)
    -- node[above right] {$c$} (C)
    -- node[left] {$b$} cycle;
  % right-angle mark
  \\draw (0.35,0) -- (0.35,0.35) -- (0,0.35);
\\end{tikzpicture}`,
  },
  {
    name: 'Venn (2 sets)',
    code: `\\begin{tikzpicture}[scale=1.1]
  \\draw[thick] (-3,-2.2) rectangle (3.6,2.2);
  \\node at (3.3,1.9) {$U$};
  \\draw[thick] (-0.8,0) circle (1.5cm);
  \\draw[thick] (1.4,0) circle (1.5cm);
  \\node at (-1.6,1.2) {$A$};
  \\node at (2.2,1.2) {$B$};
  \\node at (0.3,0) {$A \\cap B$};
\\end{tikzpicture}`,
  },
  {
    name: 'Axes & line',
    code: `\\begin{tikzpicture}[scale=0.9]
  \\draw[->] (-3,0) -- (3.4,0) node[right] {$x$};
  \\draw[->] (0,-3) -- (0,3.4) node[above] {$y$};
  \\foreach \\i in {-2,-1,1,2} {
    \\draw (\\i,0.08) -- (\\i,-0.08) node[below] {\\i};
    \\draw (0.08,\\i) -- (-0.08,\\i) node[left] {\\i};
  }
  \\draw[thick] (-2.6,-1.6) -- (2.6,3.6) node[right] {$y = x + 1$};
\\end{tikzpicture}`,
  },
  {
    name: 'Parallel lines',
    code: `\\begin{tikzpicture}[scale=1.1]
  \\draw[thick] (-3,1) -- (3,1);
  \\draw[thick] (-3,-1) -- (3,-1);
  \\draw[thick] (-2,-2.2) -- (2,2.2);
  \\node at (1.55,1.35) {$a$};
  \\node at (0.7,-0.65) {$b$};
\\end{tikzpicture}`,
  },
  {
    name: 'Blank',
    code: `\\begin{tikzpicture}[scale=1.2]
  % Draw here. Examples:
  % \\draw[thick] (0,0) circle (1.5cm);
  % \\draw[->] (0,0) -- (2,1) node[right] {$v$};
  % \\fill (0,0) circle (2pt) node[below] {$P$};

\\end{tikzpicture}`,
  },
];

// Module-level cache: inlined fonts.css (fetched once per session)
let fontCssCache = null;

async function fetchFontCssInlined() {
  if (fontCssCache) return fontCssCache;
  const cssText = await fetch(`${TIKZJAX_BASE}/fonts.css`).then(r => r.text());
  // Inline every url(...) as a base64 data URI so the SVG is self-contained
  const urlRegex = /url\((['"]?)([^)'"]+)\1\)/g;
  const urls = [...cssText.matchAll(urlRegex)].map(m => m[2]);
  const unique = [...new Set(urls)];
  const dataUris = {};
  await Promise.all(
    unique.map(async u => {
      const abs = new URL(u, `${TIKZJAX_BASE}/fonts.css`).href;
      const blob = await fetch(abs).then(r => r.blob());
      dataUris[u] = await new Promise(res => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.readAsDataURL(blob);
      });
    })
  );
  fontCssCache = cssText.replace(urlRegex, (_, q, u) => `url(${dataUris[u] || u})`);
  return fontCssCache;
}

// Serialize the rendered SVG (with fonts inlined when possible) → PNG data URL
async function svgToPng(svgEl) {
  const rect = svgEl.getBoundingClientRect();
  const width = Math.max(1, Math.ceil(rect.width));
  const height = Math.max(1, Math.ceil(rect.height));

  const clone = svgEl.cloneNode(true);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', width);
  clone.setAttribute('height', height);

  // Best effort: embed TikZJax fonts so labels rasterize correctly.
  try {
    const css = await fetchFontCssInlined();
    const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
    style.textContent = css;
    clone.insertBefore(style, clone.firstChild);
  } catch (e) {
    // Fonts unavailable — shapes still export; labels fall back to serif.
  }

  const svgStr = new XMLSerializer().serializeToString(clone);
  const svgUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgStr)))}`;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width * EXPORT_SCALE;
      canvas.height = height * EXPORT_SCALE;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(EXPORT_SCALE, EXPORT_SCALE);
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Could not rasterize diagram'));
    img.src = svgUrl;
  });
}

// srcdoc for the sandboxed preview iframe — TikZJax compiles on load
function buildSrcDoc(code) {
  // </script> inside user code would terminate the tikz block early
  const safe = code.replace(/<\/script/gi, '<\\/script');
  return `<!DOCTYPE html><html><head>
<link rel="stylesheet" href="${TIKZJAX_BASE}/fonts.css">
<script src="${TIKZJAX_BASE}/tikzjax.js"></script>
<style>html,body{margin:0;padding:12px;background:#fff;display:flex;justify-content:center;align-items:center;min-height:100%}</style>
</head><body>
<script type="text/tikz">
${safe}
</script>
</body></html>`;
}

export default function DiagramModal({ isOpen, onClose, onInsert }) {
  const [mode, setMode] = useState('draw'); // 'draw' | 'tikz'
  const [shapeCount, setShapeCount] = useState(0);
  const drawRef = useRef(null);

  // --- TikZ tab state ---
  const [code, setCode] = useState(TEMPLATES[0].code);
  const [srcDoc, setSrcDoc] = useState('');
  const [status, setStatus] = useState('idle'); // idle | rendering | ready | error | exporting
  const iframeRef = useRef(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setMode('draw');
      setSrcDoc('');
      setStatus('idle');
      setShapeCount(0);
    }
    return stopPolling;
  }, [isOpen]);

  const startRender = useCallback(codeStr => {
    stopPolling();
    setStatus('rendering');
    setSrcDoc(buildSrcDoc(codeStr));

    const startedAt = Date.now();
    pollRef.current = setInterval(() => {
      const doc = iframeRef.current?.contentDocument;
      const svg = doc?.querySelector('svg');
      if (svg && svg.getBoundingClientRect().width > 0) {
        stopPolling();
        setStatus('ready');
      } else if (Date.now() - startedAt > RENDER_TIMEOUT_MS) {
        stopPolling();
        setStatus('error');
      }
    }, 300);
  }, []);

  const handleRender = () => startRender(code);

  const handleInsert = async () => {
    if (mode === 'draw') {
      if (!drawRef.current || drawRef.current.isEmpty()) return;
      setStatus('exporting');
      try {
        const dataUrl = await drawRef.current.exportPng();
        onInsert({ dataUrl, name: 'diagram.png', source: 'draw' });
        onClose();
      } catch (e) {
        setStatus('error');
      }
      return;
    }
    const svg = iframeRef.current?.contentDocument?.querySelector('svg');
    if (!svg) return;
    setStatus('exporting');
    try {
      const dataUrl = await svgToPng(svg);
      onInsert({ dataUrl, name: 'tikz-diagram.png', source: code });
      onClose();
    } catch (e) {
      setStatus('error');
    }
  };

  const insertDisabled =
    mode === 'draw'
      ? shapeCount === 0 || status === 'exporting'
      : status !== 'ready' && status !== 'exporting';

  if (!isOpen) return null;

  return (
    <div className="equation-modal-overlay" onClick={onClose}>
      <div className="equation-modal equation-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="equation-modal-header">
          <h3>Insert Diagram</h3>
          <div className="diagram-mode-tabs">
            <button
              type="button"
              className={`diagram-mode-tab ${mode === 'draw' ? 'active' : ''}`}
              onClick={() => setMode('draw')}
            >
              ✏️ Draw
            </button>
            <button
              type="button"
              className={`diagram-mode-tab ${mode === 'tikz' ? 'active' : ''}`}
              onClick={() => setMode('tikz')}
            >
              {'{ } '}TikZ Code
            </button>
          </div>
          <button type="button" className="equation-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="equation-modal-body">
          {mode === 'draw' ? (
            <DrawCanvas ref={drawRef} onShapesChange={setShapeCount} />
          ) : (
            <>
              {/* Templates */}
              <div className="symbol-categories">
                {TEMPLATES.map(t => (
                  <button
                    key={t.name}
                    type="button"
                    className="symbol-category-btn"
                    onClick={() => {
                      setCode(t.code);
                      setSrcDoc('');
                      setStatus('idle');
                    }}
                  >
                    {t.name}
                  </button>
                ))}
              </div>

              <div className="diagram-editor-row">
                {/* Code */}
                <div className="diagram-code-col">
                  <label className="equation-input-label">TikZ Code</label>
                  <textarea
                    className="diagram-code-input"
                    value={code}
                    onChange={e => setCode(e.target.value)}
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleRender}
                    disabled={status === 'rendering' || status === 'exporting'}
                  >
                    {status === 'rendering' ? 'Compiling…' : '▶ Render Preview'}
                  </button>
                </div>

                {/* Preview */}
                <div className="diagram-preview-col">
                  <label className="equation-input-label">Preview</label>
                  <div className="diagram-preview-frame">
                    {srcDoc ? (
                      <iframe
                        ref={iframeRef}
                        title="TikZ preview"
                        sandbox="allow-scripts allow-same-origin"
                        srcDoc={srcDoc}
                      />
                    ) : (
                      <em className="equation-preview-placeholder">
                        Pick a template or write TikZ, then Render Preview
                      </em>
                    )}
                    {status === 'rendering' && (
                      <div className="diagram-preview-status">Compiling LaTeX in browser…</div>
                    )}
                    {status === 'error' && (
                      <div className="diagram-preview-status diagram-preview-status--error">
                        Could not compile. Check your TikZ syntax (only core TikZ is supported — no
                        \usepackage).
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="equation-help">
                <strong>Tips:</strong> Diagrams save as images, so they render identically on
                mobile. Use <code>circle (2cm)</code>, <code>--</code> for lines, <code>node</code>{' '}
                for labels, <code>arc</code> for angles. First compile downloads the TeX engine (~a
                few seconds).
              </div>
            </>
          )}
        </div>

        <div className="equation-modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleInsert}
            disabled={insertDisabled}
          >
            {status === 'exporting' ? 'Exporting…' : 'Use Diagram'}
          </button>
        </div>
      </div>
    </div>
  );
}

export { TEMPLATES as DIAGRAM_TEMPLATES };
